import { CustomLink } from '@/components/custom-link/CustomLink';
import { GlobalClassName } from '@/shared/constant/GlobalClassName';
import type { NavbarFolderItem, NavbarLinkItem } from '@/shared/constant/navbar/NavbarItem';
import { NavbarItemTypeEnum } from '@/shared/constant/navbar/NavbarItem';
import { NavLinkTargetOptions } from '@tarslib/types/lib/publishedProject';
import { FoxIconArrowNarrowUpRight } from 'aui-biz-icons';
import clsx from 'clsx';
import React from 'react';
import type { NavbarGroupTypeLevel } from '../../type/NavbarGroupTypeLevel';
import { NavbarGroupLevel, NavbarGroupType } from '../../type/NavbarGroupTypeLevel';
import { IconLabel } from '../icon-label/IconLabel';

export type NavbarElementProps = {
  value: NavbarFolderItem | NavbarLinkItem;
  groupLevelType: NavbarGroupTypeLevel;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  subfix?: React.ReactNode;
  noBold?: boolean;
  className?: string;
};

export function NavbarElement(props: NavbarElementProps) {
  const { value, groupLevelType, subfix, onClick, noBold, className } = props;
  const isMaxMdShowIconBorder =
    groupLevelType.level !== NavbarGroupLevel.Level1 &&
    groupLevelType.type !== NavbarGroupType.Select;
  const selected = value.type === NavbarItemTypeEnum.Link ? false : value.selected;
  const [isSameOrigin, setIsSameOrigin] = React.useState(false);

  React.useEffect(() => {
    if (value.type === NavbarItemTypeEnum.Link) {
      if (value.to.startsWith(window.location.origin)) {
        setIsSameOrigin(true);
      }
    }
  }, [value]);

  const target = React.useMemo(() => {
    if (value.type === NavbarItemTypeEnum.Link) {
      if (isSameOrigin) {
        return NavLinkTargetOptions.SELF;
      } else {
        return value.target;
      }
    }

    return undefined;
  }, [isSameOrigin, value]);

  return React.createElement(
    value.to && !value.disabled ? CustomLink : 'span',
    {
      to: value.to,
      disabled: value.disabled,
      className: clsx(
        'relative inline-flex max-w-full flex-shrink-0 items-center os:max-w-[192px]',
        groupLevelType.level === NavbarGroupLevel.Level3 ? 'px-2.5 py-0.5' : 'px-3 py-[5px]',
        isMaxMdShowIconBorder ? 'max-os:px-0 max-os:py-1' : 'max-os:py-2',
        selected &&
          groupLevelType.level === NavbarGroupLevel.Level3 &&
          'os:rounded-lg os:bg-primary-1',
        {
          ['cursor-not-allowed']: value.disabled,
        },
        {
          [GlobalClassName.HeaderTopNavbarLeftItem]:
            groupLevelType.level === NavbarGroupLevel.Level1,
          [GlobalClassName.HeaderTopNavbarLeftItemActive]:
            groupLevelType.level === NavbarGroupLevel.Level1 && selected,
          [GlobalClassName.HeaderSecondlyNavbarItem]:
            groupLevelType.level === NavbarGroupLevel.Level2 &&
            groupLevelType.type !== NavbarGroupType.Select,
          [GlobalClassName.HeaderSecondlyNavbarItemActive]:
            groupLevelType.level === NavbarGroupLevel.Level2 &&
            groupLevelType.type !== NavbarGroupType.Select &&
            selected,
          [GlobalClassName.HeaderTertiaryNavbarItem]:
            groupLevelType.level === NavbarGroupLevel.Level3,
          [GlobalClassName.HeaderTertiaryNavbarItemActive]:
            groupLevelType.level === NavbarGroupLevel.Level3 && selected,
        },
        className,
      ),
      target,
      onClick: onClick,
      title: value.title,
    },
    <>
      <IconLabel
        icon={value.icon}
        label={value.title}
        selected={selected}
        isMaxMdShowIconBorder={isMaxMdShowIconBorder}
        isBold={
          !noBold &&
          groupLevelType.level === NavbarGroupLevel.Level1 &&
          groupLevelType.type === NavbarGroupType.Default
        }
        subfix={
          subfix ??
          (value.type === NavbarItemTypeEnum.Link && !isSameOrigin ? (
            <FoxIconArrowNarrowUpRight size={12} className="text-disabled" />
          ) : undefined)
        }
      />

      {selected &&
        groupLevelType.level !== NavbarGroupLevel.Level3 &&
        groupLevelType.type === NavbarGroupType.LineTab && (
          <div className="absolute bottom-[-9px] left-0 h-[2px] w-full rounded-t-full bg-primary max-os:hidden" />
        )}
    </>,
  );
}
